/**
 * Date/time formatting for Flô chat messages (pt-BR, the browser's local timezone).
 * Server timestamps arrive as UTC ISO strings and render in local time; live messages
 * are stamped client-side. Every function tolerates a missing/invalid input.
 */

function parse(iso: string | undefined | null): Date | null {
	if (!iso) return null;
	const d = new Date(iso);
	return isNaN(d.getTime()) ? null : d;
}

function sameCalendarDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear()
		&& a.getMonth() === b.getMonth()
		&& a.getDate() === b.getDate();
}

/** Clock time of a message, e.g. "14:33". Empty when the timestamp is missing/invalid. */
export function messageTime(iso: string | undefined | null): string {
	const d = parse(iso);
	return d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
}

/** Full date + time for the hover title, e.g. "15 de junho de 2026, 14:33". */
export function messageTimestampFull(iso: string | undefined | null): string {
	const d = parse(iso);
	return d ? d.toLocaleString('pt-BR', {
		day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
	}) : '';
}

/** Whether two timestamps fall on the same calendar day (local). False if either is missing. */
export function sameDay(a: string | undefined | null, b: string | undefined | null): boolean {
	const da = parse(a);
	const db = parse(b);
	return !!da && !!db && sameCalendarDay(da, db);
}

/** Day-divider label: "Hoje", "Ontem", or a date ("12 de junho", with the year when not the current one). */
export function daySeparatorLabel(iso: string | undefined | null): string {
	const d = parse(iso);
	if (!d) return '';

	const today = new Date();
	if (sameCalendarDay(d, today)) return 'Hoje';

	const yesterday = new Date(today);
	yesterday.setDate(today.getDate() - 1);
	if (sameCalendarDay(d, yesterday)) return 'Ontem';

	const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
	if (d.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
	return d.toLocaleDateString('pt-BR', opts);
}

// ─── Thread history helpers ────────────────────────────────────────────────

/** Calendar days between the thread's last activity ISO timestamp and today (0 = today).
 *  Uses sameCalendarDay comparisons instead of raw millisecond division to stay
 *  DST-robust across spring/autumn clock changes. */
export function daysAgo(iso: string): number {
	const d = parse(iso);
	if (!d) return 0;
	const today = new Date();
	// Walk backwards from today until we find the calendar day that matches — capped at 400.
	for (let n = 0; n <= 400; n++) {
		const candidate = new Date(today);
		candidate.setDate(today.getDate() - n);
		if (sameCalendarDay(d, candidate)) return n;
		// Once we've walked past d's calendar day, stop.
		const candidateMidnight = new Date(candidate.getFullYear(), candidate.getMonth(), candidate.getDate());
		if (d < candidateMidnight) continue;
		break;
	}
	// Fallback for timestamps more than 400 calendar days ago (midnight-to-midnight arithmetic).
	const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const dMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
	return Math.round((todayMidnight.getTime() - dMidnight.getTime()) / 86_400_000);
}

/** Maps a day-offset to the recency bucket label used in the thread history list. */
export function threadGroupLabel(days: number): string {
	if (days <= 0) return 'Hoje';
	if (days === 1) return 'Ontem';
	if (days <= 7) return 'Últimos 7 dias';
	return 'Anteriores';
}

/** Display label shown on each thread row: clock time (today), weekday abbrev (≤7 days), or
 *  short date (older). Mirrors the same timezone logic as daysAgo. */
export function threadTimeLabel(iso: string): string {
	const d = parse(iso);
	if (!d) return '';
	const days = daysAgo(iso);
	if (days <= 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
	if (days <= 7) return d.toLocaleDateString('pt-BR', { weekday: 'short' });
	return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Canonical display order for thread recency groups. */
export const THREAD_GROUP_ORDER: readonly string[] = ['Hoje', 'Ontem', 'Últimos 7 dias', 'Anteriores'];
